import { FC, useEffect, ReactNode, useState, Fragment } from "react"

import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import { useMediaQuery, useTheme } from "@mui/material"

import { Typography, Button, Tag, Skeleton } from "@synapse-analytics/synapse-ui"

import styles from "./StatusScreen.module.scss"

/**
 * Props for the StatusScreen component
 * @interface StatusScreenProps
 */
interface StatusScreenProps {
  /** Main title displayed on the screen */
  mainTitle: string
  /** Subtitle displayed beside the main title */
  subTitle?: string
  /** Optional sub-content displayed below the main title */
  content?: string
  /** Optional list of infoList to display */
  infoList?: string[]
  /** Text to display on the action button */
  buttonText: string
  /** Function to call when the button is clicked */
  buttonAction: () => void
  /** Visual component to display (image, illustration, etc.) */
  VisualDescription: ReactNode | string
  /** Whether the screen is a lock screen with different styling */
  isLockScreen?: boolean
}

/**
 * StatusScreen component displays a status content with optional details and actions
 *
 * @component
 * @example
 * ```tsx
 * <StatusScreen
 *   mainTitle="Access Denied"
 *   content="You don't have permission to view this content"
 *   infoList={["Your account doesn't have the required role", "Contact your administrator"]}
 *   buttonText="Go Back"
 *   buttonAction={() => navigate(-1)}
 *   VisualDescription={<LockedImage />}
 *   isLockScreen={true}
 * />
 * ```
 */
const StatusScreen: FC<StatusScreenProps> = ({
  mainTitle,
  subTitle,
  content,
  infoList = [],
  buttonText,
  buttonAction,
  VisualDescription,
  isLockScreen = false,
}) => {
  const theme = useTheme()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"))

  // State to track image load error
  const [imageError, setImageError] = useState(false)
  const [imageLoading, setImageLoading] = useState(true)

  /**
   * Resets image error state when VisualDescription changes (e.g. new image URL).
   */
  useEffect(() => {
    if (typeof VisualDescription === "string" && VisualDescription.startsWith("http")) {
      setImageError(false)
    }
  }, [VisualDescription])

  /**
   * Sets document body background by screen type (lock vs regular); cleans up on unmount.
   */
  useEffect(() => {
    const backgroundStyle = isLockScreen
      ? "var(--base-background-3)"
      : "linear-gradient(165deg , #f5f8fe 50%, #e2e9fd  50.3%); background-repeat: no-repeat; background-attachment:fixed;"

    document.body.setAttribute("style", `margin: 0; height: 100vh; background: ${backgroundStyle}`)

    // Cleanup: remove background when component unmounts
    return () => {
      document.body.setAttribute("style", "")
    }
  }, [isLockScreen])

  return (
    <div className={`${styles.screenWrapper} ${!isLockScreen ? styles.regularScreen : ""}`}>
      <div className={`${styles.container} ${isLockScreen ? styles.lockContainer : styles.regularContainer}`}>
        <div className={`${styles.contentSection} ${isLockScreen ? styles.lockContentSection : ""}`}>
          <div className={`${styles.contentWrapper} ${isLockScreen ? styles.lockContentWrapper : ""}`}>
            {!isLockScreen && (
              <header className={styles.brandHeader}>
                <Typography className={styles.brandTitle} color="important" variantColor={2} variant="h1-bold">
                  AZKAVISION
                </Typography>
              </header>
            )}
            <div className={styles.titleSection}>
              <Typography className={styles.mainTitle} variant="h1-bold" variantColor={isLockScreen ? 2 : 1}>
                {mainTitle}
              </Typography>
              <Typography variant="h1-bold" variantColor={2}>
                {subTitle}
              </Typography>
              {isLockScreen && (
                <Tag color="orange">
                  <LockOutlinedIcon className={styles.lockIcon} />
                  Locked
                </Tag>
              )}
            </div>
            <div className={styles.messageSection}>
              {content && (
                <Typography className={styles.content} variant="h2-regular">
                  {content}
                </Typography>
              )}
              {infoList.length > 0 && (
                <ul className={styles.reasonsList}>
                  {infoList.map((reason, index) => (
                    <li key={index}>
                      <Typography variant="h3-regular" variantColor={2}>
                        {reason}
                      </Typography>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <Button onClick={buttonAction} variant="primary">
            {buttonText}
          </Button>
        </div>
        {!isMediumScreen && (
          <div className={`${styles.artContainer} ${isLockScreen ? styles.lockArtSection : styles.artSection}`}>
            {VisualDescription && typeof VisualDescription === "string" && VisualDescription.startsWith("http") ? (
              imageError ? (
                // TEMPORARY: This is a placeholder until the design for the empty state is ready.
                // For now, we hide the element. Replace with proper empty state when design is available.
                <div className={styles.emptyState} style={{ visibility: "hidden" }}>
                  {/* 
                    <ImageConnectionError />
                    <Typography variant="label-regular">Couldn't load the image..</Typography>
                  */}
                </div>
              ) : (
                <Fragment>
                  {imageLoading && <Skeleton variant="rectangular" width={758} height={440} />}
                  <img
                    src={VisualDescription}
                    alt="Visual Description"
                    className={styles.visualImage}
                    onError={() => setImageError(true)}
                    onLoadStart={() => setImageLoading(true)}
                    onLoad={() => setImageLoading(false)}
                    style={{ display: imageLoading ? "none" : "block" }}
                  />
                </Fragment>
              )
            ) : (
              VisualDescription
            )}
          </div>
        )}
      </div>
      {!isLockScreen && (
        <div className={styles.footer}>
          <a
            href="https://www.synapse-analytics.io/"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            <Typography className={styles.footerText} variant="h1-bold" align="center">
              <span className={styles.footerLight}>SYNAPSE</span>
              <br />
              ANALYTICS
            </Typography>
          </a>
        </div>
      )}
    </div>
  )
}

export default StatusScreen
